import { useState } from 'react';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import firebaseApp from '../firebase';
import { useNavigate } from 'react-router-dom';
import { validateEmail, sanitizeInput, checkRateLimit } from '../utils/security';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    // Rate limiting check (5 attempts per minute)
    if (!checkRateLimit('login', 5, 60000)) {
      setError('Too many login attempts. Please try again in a minute.');
      setLoading(false);
      return;
    }

    // Sanitize and validate inputs
    const sanitizedEmail = sanitizeInput(email.toLowerCase().trim());
    
    if (!validateEmail(sanitizedEmail)) {
      setError('Please enter a valid email address.');
      setLoading(false);
      return;
    }

    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters.');
      setLoading(false);
      return;
    }

    try {
      const auth = getAuth(firebaseApp);
      await signInWithEmailAndPassword(auth, sanitizedEmail, password);
      setSuccess(true);
      
      // Clear sensitive data
      setPassword('');
      setEmail('');
      
      // Navigate after a brief delay
      setTimeout(() => {
        navigate('/manage-places', { replace: true });
      }, 500);
    } catch (err) {
      setAttempts(prev => prev + 1);
      
      // Provide specific error messages while being security-conscious
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        setError('Invalid email or password.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else if (err.code === 'auth/network-request-failed') {
        setError('Network error. Please check your connection.');
      } else {
        setError('Login failed. Please try again.');
      }
      
      // Clear password on failed attempt
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-20 min-h-screen">
      <div className="container-width section-padding max-w-md mx-auto">
        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl shadow-xl p-8">
          <h2 className="text-3xl font-bold text-white mb-6 text-center">Admin Login</h2>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-200 mb-2">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-gray-900/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                placeholder="your@email.com"
                autoComplete="username"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-200 mb-2">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-gray-900/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                placeholder="Password"
                autoComplete="current-password"
              />
            </div>
            {error && <div className="text-red-400 text-sm text-center bg-red-900/30 border border-red-700 rounded-lg p-3">{error}</div>}
            {success && <div className="text-green-400 text-sm text-center bg-green-900/30 border border-green-700 rounded-lg p-3">Login successful!</div>}
            <button
              type="submit"
              className="w-full btn-primary py-3 text-lg"
              disabled={loading}
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Login;